import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import VideoRow from "../components/VideoRow";
import apiClient from "../api/apiClient";
import { useLanguage } from "../i18n/LanguageContext";

// ─── Dummy data (used when API is unreachable for layout preview) ─────────────

const DUMMY_CATEGORIES = [
  {
    id: 1,
    name: "Biceps",
    videos: [
      { id: 1, slug: "bicep-curl",    title: "Barbell Bicep Curl",      thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Bicep+Curl" },
      { id: 2, slug: "hammer-curl",   title: "Hammer Curl",             thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Hammer+Curl" },
      { id: 3, slug: "incline-curl",  title: "Incline Dumbbell Curl",   thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Incline+Curl" },
    ],
    children: [],
  },
  {
    id: 2,
    name: "Chest",
    children: [
      {
        id: 21,
        name: "Upper Chest",
        videos: [
          { id: 4, slug: "incline-bench", title: "Incline Bench Press",    thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Incline+Press" },
          { id: 5, slug: "incline-fly",   title: "Incline Dumbbell Fly",   thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Incline+Fly" },
        ],
      },
      {
        id: 22,
        name: "Lower Chest",
        videos: [
          { id: 6, slug: "decline-press",   title: "Decline Bench Press", thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Decline+Press" },
          { id: 7, slug: "cable-crossover", title: "Cable Crossover",      thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Cable+Crossover" },
        ],
      },
    ],
    videos: [],
  },
  {
    id: 3,
    name: "Back",
    videos: [
      { id: 8,  slug: "deadlift",    title: "Conventional Deadlift", thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Deadlift" },
      { id: 9,  slug: "pull-up",     title: "Pull Up",               thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Pull+Up" },
      { id: 10, slug: "bent-row",    title: "Bent Over Row",         thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Bent+Row" },
      { id: 11, slug: "lat-pulldown", title: "Lat Pulldown",         thumbnail: "https://placehold.co/260x160/1a1a1a/ff0033?text=Lat+Pulldown" },
    ],
    children: [],
  },
];

// ─── Subscription Gate Modal ──────────────────────────────────────────────────

const SubscriptionModal = ({ libraryName, packages, onClose }) => {
  const navigate    = useNavigate();
  const isLoggedIn  = !!localStorage.getItem("token");
  const { t } = useLanguage();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "32px 28px",
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 18,
            background: "none",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
            color: "#888",
            lineHeight: 1,
          }}
          aria-label={t("Close")}
        >
          &times;
        </button>

        {/* Heading */}
        <div className="text-center mb-4">
          <span
            style={{
              color: "var(--primary)",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
            }}
          >
            {t("Subscription Required")}
          </span>
          <h4 style={{ marginTop: 6, marginBottom: 6 }}>
            {t("Unlock {name}", { name: libraryName })}
          </h4>
          <p className="text-muted" style={{ fontSize: 14 }}>
            {isLoggedIn
              ? t("Choose a plan below to get access.")
              : t("Log in or create an account, then subscribe to get access.")}
          </p>
        </div>

        {/* Package cards */}
        {packages.length === 0 ? (
          <p className="text-center text-muted">{t("No packages available at the moment.")}</p>
        ) : (
          <div className="row g-3">
            {packages.map((pkg) => {
              const libs = pkg.libraries ?? [];
              return (
                <div className="col-sm-6" key={pkg.id}>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 10,
                      padding: "20px 16px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Package name + price */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 style={{ margin: 0, fontWeight: 700 }}>{pkg.name}</h6>
                      <span
                        style={{
                          color: "var(--primary)",
                          fontWeight: 700,
                          fontSize: 15,
                          whiteSpace: "nowrap",
                          marginLeft: 8,
                        }}
                      >
                        ${pkg.price}
                      </span>
                    </div>

                    <p
                      className="text-muted"
                      style={{ fontSize: 12, margin: "0 0 10px" }}
                    >
                      {t("{days} days", { days: pkg.duration_days })}
                    </p>

                    {/* Accessible libraries */}
                    {libs.length > 0 && (
                      <div className="mb-3">
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color: "#888",
                            margin: "0 0 4px",
                          }}
                        >
                          {t("Includes access to")}
                        </p>
                        <div className="d-flex flex-wrap gap-1">
                          {libs.map((lib) => (
                            <span
                              key={lib.id}
                              style={{
                                background:
                                  lib.slug ===
                                  window.location.pathname.split("/").pop()
                                    ? "var(--primary)"
                                    : "#f0f0f0",
                                color:
                                  lib.slug ===
                                  window.location.pathname.split("/").pop()
                                    ? "#fff"
                                    : "#333",
                                fontSize: 11,
                                padding: "3px 9px",
                                borderRadius: 4,
                                fontWeight: 500,
                              }}
                            >
                              {lib.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bullet points */}
                    {pkg.description_points?.length > 0 && (
                      <ul
                        style={{
                          paddingLeft: 16,
                          fontSize: 12,
                          color: "#555",
                          margin: "0 0 12px",
                          flexGrow: 1,
                        }}
                      >
                        {pkg.description_points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    )}

                    <div style={{ marginTop: "auto" }}>
                      {pkg.is_ultimate_program ? (
                        <button
                          className="btn btn-primary btn-skew w-100"
                          style={{ fontSize: 13 }}
                          onClick={() => navigate("/program-inquiry")}
                        >
                          <span>{t("Request Your Program")}</span>
                        </button>
                      ) : isLoggedIn ? (
                        <button
                          className="btn btn-primary btn-skew w-100"
                          style={{ fontSize: 13 }}
                          onClick={() => navigate("/auth?action=subscribe&pkg=" + pkg.id)}
                        >
                          <span>{t("Subscribe")}</span>
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-primary btn-skew w-100"
                          style={{ fontSize: 13 }}
                          onClick={() => navigate("/auth")}
                        >
                          <span>{t("Log In to Subscribe")}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── LibraryPage ─────────────────────────────────────────────────────────────

const LibraryPage = () => {
  const { slug }   = useParams();
  const location   = useLocation();
  const { t } = useLanguage();

  const [categories, setCategories]         = useState([]);
  const [libraryName, setLibraryName]       = useState("");
  const [availablePackages, setAvailablePackages] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [showSubModal, setShowSubModal]     = useState(false);
  const [videoModal, setVideoModal]         = useState(null);
  const [videoLoading, setVideoLoading]     = useState(false);

  // Apply theme
  useEffect(() => {
    document.body.setAttribute("data-theme-color", "color_2");
    localStorage.setItem("theme", "color_2");
    localStorage.setItem("themeInd", 1);
  }, [location.pathname]);

  // Fetch library data
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setShowSubModal(false);

    Promise.all([
      apiClient.get(`/libraries/${slug}`),
      apiClient.get(`/libraries/${slug}/categories`),
    ])
      .then(([libRes, catRes]) => {
        setLibraryName(libRes.data.data?.name || slug);
        const cats = catRes.data.data?.length ? catRes.data.data : DUMMY_CATEGORIES;
        setCategories(cats);
        if (cats.length > 0) setActiveCategory(cats[0]);
      })
      .catch((err) => {
        const status = err.response?.status;

        if (status === 403 || status === 401) {
          // Not subscribed (or not logged in) — fetch the library's packages to show in modal
          apiClient
            .get(`/libraries/${slug}`)
            .then((libRes) => {
              const data = libRes.data.data ?? {};
              setLibraryName(data.name || slug);
              setAvailablePackages(data.packages ?? []);
            })
            .catch(() => setLibraryName(slug));

          setShowSubModal(true);
        } else {
          // API unavailable — fall back to dummy data for layout preview
          setCategories(DUMMY_CATEGORIES);
          setActiveCategory(DUMMY_CATEGORIES[0]);
          setLibraryName(slug);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handlePlay = async (video) => {
    setVideoLoading(true);
    try {
      const res = await apiClient.get(`/videos/${video.slug}`);
      const embedUrl = res.data.data?.embed_url;
      if (embedUrl) setVideoModal({ embedUrl, title: video.title });
    } catch {
      // video not accessible
    } finally {
      setVideoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <p>{t("Loading library…")}</p>
      </div>
    );
  }

  const renderContent = () => {
    if (!activeCategory) return <div className="text-muted">{t("No category selected")}</div>;

    const children = activeCategory.children || [];
    if (children.length > 0) {
      return children.map((sub) => (
        <VideoRow
          key={sub.id}
          title={sub.name}
          iconUrl={sub.icon_url}
          videos={(sub.videos || []).map(normalizeVideo)}
          onPlay={handlePlay}
        />
      ));
    }

    const videos = (activeCategory.videos || []).map(normalizeVideo);
    if (videos.length === 0) {
      return (
        <div className="text-center text-muted mt-5">
          <h5>{t("No videos available yet")}</h5>
          <p>{t("Content will be added soon.")}</p>
        </div>
      );
    }

    return (
      <VideoRow
        title={activeCategory.name}
        iconUrl={activeCategory.icon_url}
        eyebrow={t("Category")}
        videos={videos}
        onPlay={handlePlay}
      />
    );
  };

  return (
    <>
      <div className="container py-5">

        {/* HEADER */}
        <div className="text-center mb-5">
          <span
            style={{
              color: "var(--primary)",
              fontWeight: 700,
              letterSpacing: 3,
              fontSize: 12,
              textTransform: "uppercase",
            }}
          >
            {t("Library")}
          </span>
          <h1 className="title" style={{ marginTop: 6 }}>
            {libraryName}
          </h1>
          <p
            className="text-muted mt-2"
            style={{ maxWidth: 540, margin: "8px auto 0" }}
          >
            {t("Browse categories and videos in this library. Click a video to start watching.")}
          </p>
        </div>

        <div className="row">
          {/* SIDEBAR */}
          <div className="col-lg-3 mb-4">
            <div className="hpa-category-list" aria-label={t("Video categories")}>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`hpa-category-item ${
                    activeCategory?.id === cat.id ? "is-active" : ""
                  }`}
                  onClick={() => setActiveCategory(cat)}
                >
                  <span className="hpa-category-icon" aria-hidden="true">
                    {cat.icon_url ? (
                      <img src={cat.icon_url} alt="" />
                    ) : (
                      <i className="fa-solid fa-dumbbell" />
                    )}
                  </span>
                  <span className="hpa-category-name">{cat.name}</span>
                  <i className="fa-solid fa-chevron-right hpa-category-arrow" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          {/* CONTENT */}
          <div className="col-lg-9">
            {videoLoading && <p className="text-muted mb-3">{t("Loading video…")}</p>}
            {renderContent()}
          </div>
        </div>
      </div>

      {/* VIDEO MODAL (Bunny iframe) */}
      {videoModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setVideoModal(null)}
        >
          <div
            style={{ width: "90%", maxWidth: "900px", aspectRatio: "16/9", position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoModal(null)}
              style={{
                position: "absolute",
                top: "-36px",
                right: 0,
                background: "none",
                border: "none",
                color: "#fff",
                fontSize: "24px",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
            <iframe
              src={videoModal.embedUrl}
              title={videoModal.title}
              style={{ width: "100%", height: "100%", border: "none", borderRadius: "8px" }}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* SUBSCRIPTION GATE MODAL */}
      {showSubModal && (
        <SubscriptionModal
          libraryName={libraryName}
          packages={availablePackages}
          onClose={() => setShowSubModal(false)}
        />
      )}
    </>
  );
};

const normalizeVideo = (v) => ({ ...v, thumbnail: v.thumbnail_url });

export default LibraryPage;
